import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixFastgalleryDraftitemV1DraftItem from './fastgallery-draftitem-v1-draft-item-draft-items.http.js';
// @ts-ignore
import { transformSDKImageToRESTImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/**
 * A DraftFastGalleryItem is a ...
 * You can ...
 * Read more about DraftFastGalleryItems
 * in this [article](<LINK_TO_KB_ARTICLE>).
 */
export interface DraftItem {
  /**
   * Item ID
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Represents the current state of the Item. Each time the item is modified, its `revision` changes by the server. for an update operation to succeed, you MUST pass the latest revision
   * @readonly
   */
  revision?: string | null;
  /**
   * Gallery ID that the item is part of
   * @format GUID
   * @immutable
   */
  draftGalleryId?: string | null;
  /** @maxLength 50 */
  title?: string | null;
  /** @maxLength 1500 */
  description?: string | null;
  /** Link to an internal or external page */
  link?: Link;
  /** media of the item */
  media?: MediaItem;
  /**
   * Sort order of Item in its Gallery, needed for Internal use
   * @internal
   * @readonly
   */
  sortOrder?: number | null;
  /**
   * Represents the time this Item was created
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Represents the time this Item was last updated
   * @readonly
   */
  _updatedDate?: Date | null;
  /** Data Extensions */
  extendedFields?: ExtendedFields;
}

export interface Link {
  /**
   * The type of the link
   * @internal
   */
  type?: LinkTypeWithLiterals;
  /**
   * Display text of the link.
   * `referenced_entity_field` carries this field's translations from a source entity onto an entity
   * that references it - concretely, DraftItem -> the published Item, whose translations are synced
   * from DraftItem rather than owned locally. Without it the translated label is served on the draft
   * read (so the editor looks right) and silently falls back to the origin language on the published
   * read, which is what site visitors get. It must be declared on this leaf: the collector only
   * honours the annotation on STRING / StringValue / RichContent and ignores it on message fields.
   * The path is the field's location in the source entity; every embedder of this type - pro-gallery
   * and fast-gallery, draft and published - places it at `link`, so one path is correct for all.
   * @maxLength 1000
   */
  text?: string | null;
  /**
   * Target URL of the link.
   * @format WEB_URL
   */
  url?: string | null;
  /**
   * Whether the link opens in a new tab or window. One of:
   * * `'_blank'`: The link opens in a new tab or window.
   * * `'_self'`: The link opens in the same tab or window.
   * if more options are needed, be sure to change maxLength accordingly
   * @internal
   * @maxLength 6
   */
  target?: string | null;
  /**
   * Information about the link. Reserved for internal use.
   * @internal
   */
  wixLinkData?: WixLink;
}

export enum LinkType {
  UNDEFINED = 'UNDEFINED',
  /** external link */
  EXTERNAL = 'EXTERNAL',
  /** for internal usage using wixLinkData */
  INTERNAL = 'INTERNAL',
}

/** @enumType */
export type LinkTypeWithLiterals =
  | LinkType
  | 'UNDEFINED'
  | 'EXTERNAL'
  | 'INTERNAL';

/** The link object generated by panels in the editor and used by applications in Wix */
export interface WixLink extends WixLinkLinkOneOf {
  /** External link type */
  external?: ExternalLink;
  /** Page link type */
  page?: PageLink;
  /** Anchor link type */
  anchor?: AnchorLink;
  /** Dynamic page link type */
  dynamicPage?: DynamicPageLink;
  /** Document link type */
  document?: DocumentLink;
  /** Email link type */
  email?: EmailLink;
  /** Phone link type */
  phone?: PhoneLink;
  /** Address link type */
  address?: AddressLink;
  /** WhatsApp link type */
  whatsApp?: WhatsAppLink;
  /** TPA link type */
  tpaPage?: TpaPageLink;
}

/** @oneof */
export interface WixLinkLinkOneOf {
  /** External link type */
  external?: ExternalLink;
  /** Page link type */
  page?: PageLink;
  /** Anchor link type */
  anchor?: AnchorLink;
  /** Dynamic page link type */
  dynamicPage?: DynamicPageLink;
  /** Document link type */
  document?: DocumentLink;
  /** Email link type */
  email?: EmailLink;
  /** Phone link type */
  phone?: PhoneLink;
  /** Address link type */
  address?: AddressLink;
  /** WhatsApp link type */
  whatsApp?: WhatsAppLink;
  /** TPA link type */
  tpaPage?: TpaPageLink;
}

export interface ExternalLink {
  /**
   * The url of the page
   * @format WEB_URL
   */
  url?: string;
  /**
   * Where this link should open, supports _self and _blank or any name the user chooses. _self means same page, _blank means new page.
   * @maxLength 1000
   */
  target?: string | null;
  /** @maxSize 20 */
  rel?: LinkRelWithLiterals[];
}

/**
 * The 'rel' attribute of the link. The rel attribute defines the relationship between a linked resource and the current document.
 * Further reading (also about different possible rel types): https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/rel
 * Following are the accepted 'rel' types by Wix applications.
 */
export enum LinkRel {
  /** Indicates that the current document's original author or publisher does not endorse the referenced document. */
  nofollow = 'nofollow',
  /** Instructs the browser to navigate to the target resource without granting the new browsing context access to the document that opened it. */
  noopener = 'noopener',
  /** No Referer header will be included. Additionally, has the same effect as noopener. */
  noreferrer = 'noreferrer',
  /** Indicates a link that resulted from advertisements or paid placements. */
  sponsored = 'sponsored',
}

/** @enumType */
export type LinkRelWithLiterals =
  | LinkRel
  | 'nofollow'
  | 'noopener'
  | 'noreferrer'
  | 'sponsored';

export interface PageLink {
  /**
   * The page id we want from the site
   * @maxLength 1000
   */
  pageId?: string;
  /**
   * Where this link should open, supports _self and _blank or any name the user chooses. _self means same page, _blank means new page.
   * @maxLength 1000
   */
  target?: string | null;
  /**
   * rel of link
   * @maxSize 20
   */
  rel?: LinkRelWithLiterals[];
}

export interface AnchorLink {
  /**
   * The name of the anchor
   * @maxLength 1000
   */
  anchorName?: string;
  /**
   * The data id (from the JSON page) of the anchor that should be used
   * @maxLength 1000
   */
  anchorDataId?: string;
  /**
   * The page id we want from the site
   * @maxLength 1000
   */
  pageId?: string;
  /**
   * rel of link
   * @maxSize 20
   */
  rel?: LinkRelWithLiterals[];
}

export interface DynamicPageLink {
  /**
   * The router that handles this link
   * @maxLength 1000
   */
  routerId?: string;
  /**
   * The path data we'd like
   * @maxLength 1000
   */
  innerRoute?: string;
  /**
   * The data id (from the JSON page) of the anchor that should be used
   * @maxLength 1000
   */
  anchorDataId?: string | null;
  /**
   * rel of link
   * @maxSize 20
   */
  rel?: LinkRelWithLiterals[];
}

export interface DocumentLink {
  /**
   * The id of the document
   * @maxLength 1000
   */
  docId?: string;
  /**
   * The name of the document for download purposes
   * @maxLength 1000
   */
  name?: string | null;
  /** If this document can be indexed by scrapers, default is false */
  indexable?: boolean;
}

export interface EmailLink {
  /**
   * The email we will be sending a message to
   * @format EMAIL
   */
  recipient?: string;
  /**
   * The subject of the email
   * @maxLength 1000
   */
  subject?: string | null;
  /**
   * The body of the email
   * @maxLength 100000
   */
  body?: string | null;
}

export interface PhoneLink {
  /**
   * The phone number we want to link to
   * @format PHONE
   * @maxLength 100
   */
  phoneNumber?: string;
}

export interface AddressLink {
  /**
   * An address that we can link to
   * @maxLength 1000
   */
  address?: string;
}

export interface WhatsAppLink {
  /**
   * The whatsApp phone number we want to connect with
   * @format PHONE
   */
  phoneNumber?: string;
}

/** Link to a TPA page */
export interface TpaPageLink {
  /**
   * Type of item (e.g. 'wix.stores.sub_pages.product')
   * @maxLength 1000
   */
  itemTypeIdentifier?: string;
  /**
   * Id of linked item
   * @maxLength 1000
   */
  itemId?: string;
  /**
   * Id of linked page
   * @maxLength 1000
   */
  pageId?: string;
  /**
   * Id of app being linked to (AppDefId)
   * @maxLength 1000
   */
  appDefinitionId?: string;
  /**
   * The relativepath of linked page
   * @maxLength 10000
   */
  path?: string;
  /**
   * rel of link
   * @maxSize 20
   */
  rel?: LinkRelWithLiterals[];
}

export interface MediaItem extends MediaItemMediaOneOf {
  /** image item */
  imageOptions?: Image;
  /** item's media type */
  type?: TypeWithLiterals;
}

/** @oneof */
export interface MediaItemMediaOneOf {
  /** image item */
  imageOptions?: Image;
}

export enum Type {
  /** undefined type */
  UNDEFINED = 'UNDEFINED',
  /** image */
  IMAGE = 'IMAGE',
}

/** @enumType */
export type TypeWithLiterals = Type | 'UNDEFINED' | 'IMAGE';

export interface Image {
  /** Information about the Wix Media image. */
  imageInfo?: string;
  /** Focal point of the image. */
  focalPoint?: Point;
  /** Set of key-value pairs describing the media in [Exchangeable Image File format](https://en.wikipedia.org/wiki/Exif). */
  exif?: Record<string, any> | null;
  /**
   * Image compression level. <br />
   *
   * Min: `30` <br />
   * Max: `100`
   * @min 30
   * @max 100
   */
  quality?: number | null;
  /** [Unsharp masking](https://en.wikipedia.org/wiki/Unsharp_masking) values of the image. */
  unsharpMasking?: UnsharpMasking;
  /**
   * Whether the image is saved in secure media.
   * @internal
   */
  secure?: boolean | null;
  /**
   * Image alt text, translatable.
   * @maxLength 5000
   */
  altText?: string | null;
}

export interface FocalPoint {
  /** X-coordinate of the focal point. */
  x?: number;
  /** Y-coordinate of the focal point. */
  y?: number;
}

export interface Point {
  /** X-coordinate of the focal point. */
  x?: number;
  /** Y-coordinate of the focal point. */
  y?: number;
}

export interface UnsharpMasking {
  /**
   * Unsharp masking amount. Controls the sharpening strength. <br />
   *
   * Min: `0` <br />
   * Max: `5`
   * @max 5
   */
  amount?: number | null;
  /** Unsharp masking radius in pixels. Controls the sharpening width. */
  radius?: number | null;
  /**
   * Unsharp masking threshold. Controls how different neighboring pixels must be for shapening to apply. <br />
   *
   * Min: `0` <br />
   * Max: `1`
   * @max 1
   */
  threshold?: number | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface ReorderedDraftItem {
  /**
   * The id of the gallery in which the reordering was done
   * @format GUID
   */
  galleryId?: string;
  /**
   * The id of the last DraftItem that was re-positioned
   * @format GUID
   */
  draftItemId?: string | null;
  /** New sortOrder of the last DraftItem that was re-positioned */
  sortOrder?: number | null;
}

export interface CreateDraftItemRequest {
  /** DraftFastGalleryItem to be created. */
  draftItem: DraftItem;
  /** If true, the item will be positioned first in the gallery. If not present or false, the items will be positioned last. */
  positionFirst?: boolean | null;
}

export interface CreateDraftItemResponse {
  /** The created DraftFastGalleryItem. */
  draftItem?: DraftItem;
}

export interface GetDraftItemRequest {
  /**
   * ID of the DraftFastGalleryItem to retrieve.
   * @format GUID
   */
  draftItemId: string;
}

export interface GetDraftItemResponse {
  /** The requested DraftFastGalleryItem. */
  draftItem?: DraftItem;
}

export interface UpdateDraftItemRequest {
  /** DraftFastGalleryItem to be updated, may be partial. */
  draftItem: DraftItem;
  /**
   * Explicit list of fields to update.
   * @internal
   */
  fieldMask?: string[];
}

export interface UpdateDraftItemResponse {
  /** Updated DraftFastGalleryItem. */
  draftItem?: DraftItem;
}

export interface DeleteDraftItemRequest {
  /**
   * Id of the DraftFastGalleryItem to delete.
   * @format GUID
   */
  draftItemId: string;
}

export interface DeleteDraftItemResponse {}

export interface QueryDraftItemsRequest {
  /** WQL expression. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryDraftItemsResponse {
  /** List of DraftFastGalleryItems. */
  draftItems?: DraftItem[];
  /** Paging metadata */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface UpdateExtendedFieldsRequest {
  /** ID of the entity to update. */
  _id: string;
  /** Identifier for the app whose extended fields are being updated. */
  namespace: string;
  /** Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured. */
  namespaceData: Record<string, any> | null;
}

export interface UpdateExtendedFieldsResponse {
  /** Updated Item. */
  draftItem?: DraftItem;
}

export interface BulkCreateDraftItemsRequest {
  /**
   * List of DraftFastGalleryItems to be created
   * @minSize 1
   * @maxSize 100
   */
  draftItems: DraftItem[];
  /** set to `true` if you wish to receive back the created DraftFastGalleryItems in the response */
  returnEntity?: boolean;
  /** If true, the item will be positioned first in the gallery. If not present or false, the items will be positioned last. */
  positionFirst?: boolean | null;
}

export interface BulkCreateDraftItemsResponse {
  /**
   * List of the bulk create operation results including the DraftFastGalleryItems and metadata.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkDraftItemResult[];
  /** Metadata regarding the bulk create operation */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkDraftItemResult {
  /** Metadata regarding the specific single create operation */
  itemMetadata?: ItemMetadata;
  /** Only exists if `returnEntity` was set to true in the request */
  item?: DraftItem;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdateDraftItemsRequest {
  /**
   * List of DraftFastGalleryItems to be updated.
   * @minSize 1
   * @maxSize 100
   */
  draftItems: MaskedDraftItem[];
  /** set to `true` if you wish to receive back the updated DraftFastGalleryItems in the response */
  returnEntity?: boolean;
}

export interface MaskedDraftItem {
  /** DraftFastGalleryItem to be updated, may be partial */
  draftItem?: DraftItem;
  /**
   * Explicit list of fields to update.
   * @internal
   */
  fieldMask?: string[];
}

export interface BulkUpdateDraftItemsResponse {
  /**
   * Results
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateDraftItemsResponseBulkDraftItemResult[];
  /** Metadata regarding the bulk update operation */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpdateDraftItemsResponseBulkDraftItemResult {
  /** Metadata regarding the specific single update operation */
  itemMetadata?: ItemMetadata;
  /** Only exists if `returnEntity` was set to true in the request */
  item?: DraftItem;
}

export interface BulkDeleteDraftItemsRequest {
  /**
   * DraftFastGalleryItem ids to be deleted
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  draftItemIds: string[];
}

export interface BulkDeleteDraftItemsResponse {
  /**
   * Results
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkDeleteDraftItemsResponseBulkDraftItemResult[];
  /** Metadata regarding the bulk delete operation */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkDeleteDraftItemsResponseBulkDraftItemResult {
  /** Metadata regarding the specific single delete operation */
  itemMetadata?: ItemMetadata;
}

export interface RemoveDraftItemFromTrashBinRequest {
  /**
   * ID of the DraftItem to remove from TrashBin
   * @format GUID
   */
  draftItemId: string;
}

export interface RemoveDraftItemFromTrashBinResponse {}

export interface RestoreDraftItemFromTrashBinRequest {
  /**
   * ID of the DraftItem to restore from TrashBin
   * @format GUID
   */
  draftItemId: string;
}

export interface RestoreDraftItemFromTrashBinResponse {
  /** The restored DraftItem */
  draftItem?: DraftItem;
}

export interface GetDeletedDraftItemRequest {
  /**
   * ID of the DraftItem to retrieve from TrashBin
   * @format GUID
   */
  draftItemId: string;
}

export interface GetDeletedDraftItemResponse {
  /** The requested DraftItem */
  draftItem?: DraftItem;
}

export interface ListDeletedDraftItemsRequest {
  /** WQL expression */
  query?: CursorQuery;
  /**
   * List of DraftItem IDs to retrieve (supersedes the query)
   * @format GUID
   * @maxSize 100
   */
  draftItemIds?: string[];
}

export interface ListDeletedDraftItemsResponse {
  /** The retrieved deleted Galleries */
  draftItems?: DraftItem[];
  /** Paging metadata */
  pagingMetadata?: CursorPagingMetadata;
}

export interface MoveDraftItemsRequest {
  /**
   * DraftItem ids to be moved after `after_item_id`, if not provided, draftItems given will be positioned first
   * @format GUID
   * @minSize 1
   * @maxSize 50
   */
  itemIds: string[];
  /**
   * Id of the draftItem which the items will be positioned after
   * @format GUID
   */
  afterItemId?: string | null;
}

export interface MoveDraftItemsResponse {}

export interface DraftItemNotFoundError {
  /**
   * The draft item id that was not found
   * @format GUID
   */
  draftItemId?: string;
}

export interface DraftItemNotInGalleryError {
  /**
   * List of gallery IDs where the draft items are located.
   * @format GUID
   * @maxSize 100000
   */
  galleryIds?: string[];
}

export interface SortDraftItemsByRequest {
  /** DraftItem ids to be sorted by field `sorting.field_name`, in direction `sorting.order` */
  sorting: SortByFieldSorting;
  /**
   * ID of the Gallery whose items are sorted
   * @format GUID
   */
  draftGalleryId: string;
}

export interface SortByFieldSorting {
  /** Name of the field to sort by. */
  fieldName?: SortableFieldNamesWithLiterals;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortableFieldNames {
  TITLE = 'TITLE',
}

/** @enumType */
export type SortableFieldNamesWithLiterals = SortableFieldNames | 'TITLE';

export interface SortDraftItemsByResponse {
  /**
   * Job ID for the sort operation (async-infra)
   * @format GUID
   */
  jobId?: string;
}

export interface DraftItemSortUnsupportedError {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type MoveDraftItemsApplicationErrors =
  | {
      code?: 'ITEM_NOT_FOUND';
      description?: string;
      data?: DraftItemNotFoundError;
    }
  | {
      code?: 'ITEMS_FROM_DIFFERENT_GALLERIES';
      description?: string;
      data?: DraftItemNotInGalleryError;
    };
/** @docsIgnore */
export type SortDraftItemsByApplicationErrors = {
  code?: 'INVALID_ARGUMENT';
  description?: string;
  data?: DraftItemSortUnsupportedError;
};

/**
 * Creates a DraftFastGalleryItem.
 *
 * The request body must include lorem and business address. Ipsum is optional.
 * @param draftItem - DraftFastGalleryItem to be created.
 * @public
 * @documentationMaturity preview
 * @requiredField draftItem
 * @permissionId FAST_GALLERY.MANAGE
 * @applicableIdentity APP
 * @returns The created DraftFastGalleryItem.
 * @fqn wix.fastgallery.draftitem.v1.DraftItemService.CreateDraftItem
 */
export async function createDraftItem(
  draftItem: DraftItem,
  options?: CreateDraftItemOptions
): Promise<
  NonNullablePaths<
    DraftItem,
    | `media.imageOptions.focalPoint.x`
    | `media.imageOptions.focalPoint.y`
    | `media.type`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      draftItem: draftItem,
      positionFirst: options?.positionFirst,
    }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [{ path: 'draftItem.media.imageOptions.imageInfo' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixFastgalleryDraftitemV1DraftItem.createDraftItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'draftItem.media.imageOptions.imageInfo' }],
        },
      ])
    )?.draftItem!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          draftItem: '$[0]',
          positionFirst: '$[1].positionFirst',
        },
        singleArgumentUnchanged: false,
      },
      ['draftItem', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CreateDraftItemOptions {
  /** If true, the item will be positioned first in the gallery. If not present or false, the items will be positioned last. */
  positionFirst?: boolean | null;
}

/**
 * Retrieves a DraftFastGalleryItem.
 * @param draftItemId - ID of the DraftFastGalleryItem to retrieve.
 * @public
 * @documentationMaturity preview
 * @requiredField draftItemId
 * @permissionId FAST_GALLERY.READ
 * @applicableIdentity APP
 * @returns The requested DraftFastGalleryItem.
 * @fqn wix.fastgallery.draftitem.v1.DraftItemService.GetDraftItem
 */
export async function getDraftItem(
  draftItemId: string
): Promise<
  NonNullablePaths<
    DraftItem,
    | `media.imageOptions.focalPoint.x`
    | `media.imageOptions.focalPoint.y`
    | `media.type`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    draftItemId: draftItemId,
  });

  const reqOpts =
    ambassadorWixFastgalleryDraftitemV1DraftItem.getDraftItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'draftItem.media.imageOptions.imageInfo' }],
        },
      ])
    )?.draftItem!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { draftItemId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['draftItemId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates a DraftFastGalleryItem.
 * @param _id - Item ID
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField draftItem
 * @requiredField draftItem.revision
 * @permissionId FAST_GALLERY.MANAGE
 * @applicableIdentity APP
 * @returns Updated DraftFastGalleryItem.
 * @fqn wix.fastgallery.draftitem.v1.DraftItemService.UpdateDraftItem
 */
export async function updateDraftItem(
  _id: string,
  draftItem: NonNullablePaths<UpdateDraftItem, `revision`, 2>
): Promise<
  NonNullablePaths<
    DraftItem,
    | `media.imageOptions.focalPoint.x`
    | `media.imageOptions.focalPoint.y`
    | `media.type`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      draftItem: { ...draftItem, id: _id },
    }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [{ path: 'draftItem.media.imageOptions.imageInfo' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixFastgalleryDraftitemV1DraftItem.updateDraftItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'draftItem.media.imageOptions.imageInfo' }],
        },
      ])
    )?.draftItem!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { draftItem: '$[1]' },
        explicitPathsToArguments: { 'draftItem.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'draftItem']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateDraftItem {
  /**
   * Item ID
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Represents the current state of the Item. Each time the item is modified, its `revision` changes by the server. for an update operation to succeed, you MUST pass the latest revision
   * @readonly
   */
  revision?: string | null;
  /**
   * Gallery ID that the item is part of
   * @format GUID
   * @immutable
   */
  draftGalleryId?: string | null;
  /** @maxLength 50 */
  title?: string | null;
  /** @maxLength 1500 */
  description?: string | null;
  /** Link to an internal or external page */
  link?: Link;
  /** media of the item */
  media?: MediaItem;
  /**
   * Sort order of Item in its Gallery, needed for Internal use
   * @internal
   * @readonly
   */
  sortOrder?: number | null;
  /**
   * Represents the time this Item was created
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Represents the time this Item was last updated
   * @readonly
   */
  _updatedDate?: Date | null;
  /** Data Extensions */
  extendedFields?: ExtendedFields;
}

/**
 * Deletes a DraftFastGalleryItem.
 * @param draftItemId - Id of the DraftFastGalleryItem to delete.
 * @public
 * @documentationMaturity preview
 * @requiredField draftItemId
 * @permissionId FAST_GALLERY.MANAGE
 * @applicableIdentity APP
 * @fqn wix.fastgallery.draftitem.v1.DraftItemService.DeleteDraftItem
 */
export async function deleteDraftItem(draftItemId: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    draftItemId: draftItemId,
  });

  const reqOpts =
    ambassadorWixFastgalleryDraftitemV1DraftItem.deleteDraftItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { draftItemId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['draftItemId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates a query to retrieve a list of draft items.
 *
 * The `queryDraftItems()` method builds a query to retrieve a list of draft items and returns a `DraftItemsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the `find()` method.
 *
 * You can refine the query by chaining `DraftItemsQueryBuilder` methods onto the query. `DraftItemsQueryBuilder` methods enable you to filter, sort, and control the results that `queryDraftItems()` returns.
 * @public
 * @documentationMaturity preview
 * @permissionId FAST_GALLERY.READ
 * @applicableIdentity APP
 * @fqn wix.fastgallery.draftitem.v1.DraftItemService.QueryDraftItems
 */
export function queryDraftItems(): DraftItemsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    DraftItem,
    'CURSOR',
    QueryDraftItemsRequest,
    QueryDraftItemsResponse
  >({
    func: async (payload: QueryDraftItemsRequest) => {
      const reqOpts =
        ambassadorWixFastgalleryDraftitemV1DraftItem.queryDraftItems(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryDraftItemsRequest['query']) => {
      const args = [query, {}] as [QueryDraftItemsRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({ data }: HttpResponse<QueryDraftItemsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [
          {
            transformFn: transformRESTImageToSDKImage,
            paths: [{ path: 'draftItems.media.imageOptions.imageInfo' }],
          },
        ])
      );

      return {
        items: transformedData?.draftItems,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface DraftItemsQueryResult extends QueryCursorResult {
  items: DraftItem[];
  query: DraftItemsQueryBuilder;
  next: () => Promise<DraftItemsQueryResult>;
  prev: () => Promise<DraftItemsQueryResult>;
}

export interface DraftItemsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  eq: (
    propertyName:
      | '_id'
      | 'draftGalleryId'
      | 'title'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => DraftItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ne: (
    propertyName:
      | '_id'
      | 'draftGalleryId'
      | 'title'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => DraftItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ge: (
    propertyName:
      | '_id'
      | 'draftGalleryId'
      | 'title'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => DraftItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  gt: (
    propertyName:
      | '_id'
      | 'draftGalleryId'
      | 'title'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => DraftItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  le: (
    propertyName:
      | '_id'
      | 'draftGalleryId'
      | 'title'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => DraftItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  lt: (
    propertyName:
      | '_id'
      | 'draftGalleryId'
      | 'title'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => DraftItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   * @documentationMaturity preview
   */
  startsWith: (
    propertyName: '_id' | 'draftGalleryId' | 'title',
    value: string
  ) => DraftItemsQueryBuilder;
  /** @documentationMaturity preview */
  in: (
    propertyName:
      | '_id'
      | 'draftGalleryId'
      | 'title'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => DraftItemsQueryBuilder;
  /** @documentationMaturity preview */
  exists: (
    propertyName:
      | '_id'
      | 'draftGalleryId'
      | 'title'
      | '_createdDate'
      | '_updatedDate',
    value: boolean
  ) => DraftItemsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  ascending: (
    ...propertyNames: Array<
      | '_id'
      | 'draftGalleryId'
      | 'title'
      | 'sortOrder'
      | '_createdDate'
      | '_updatedDate'
    >
  ) => DraftItemsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  descending: (
    ...propertyNames: Array<
      | '_id'
      | 'draftGalleryId'
      | 'title'
      | 'sortOrder'
      | '_createdDate'
      | '_updatedDate'
    >
  ) => DraftItemsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object.
   * @documentationMaturity preview
   */
  limit: (limit: number) => DraftItemsQueryBuilder;
  /** @param cursor - A pointer to specific record
   * @documentationMaturity preview
   */
  skipTo: (cursor: string) => DraftItemsQueryBuilder;
  /** @documentationMaturity preview */
  find: () => Promise<DraftItemsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.fastgallery.draftitem.v1.DraftItemService.QueryDraftItems
 * @requiredField query
 */
export async function typedQueryDraftItems(
  query: DraftItemQuery
): Promise<
  NonNullablePaths<
    QueryDraftItemsResponse,
    `draftItems` | `draftItems.${number}.media.type`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixFastgalleryDraftitemV1DraftItem.queryDraftItems(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'draftItems.media.imageOptions.imageInfo' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface DraftItemQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: [
        '_createdDate',
        '_id',
        '_updatedDate',
        'draftGalleryId',
        'title'
      ];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  DraftItem,
  DraftItemQuerySpec
>;
export type DraftItemQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object in the following format:
  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`
  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object in the following format:
  `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]` 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: /*#__PURE__*/ createQueryUtils<
    DraftItem,
    DraftItemQuerySpec,
    DraftItemQuery
  >(),
};

/**
 * Updates extended fields of a DraftFastGalleryItem without incrementing revision
 * @param _id - ID of the entity to update.
 * @param namespace - Identifier for the app whose extended fields are being updated.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField namespace
 * @requiredField options
 * @requiredField options.namespaceData
 * @permissionId FAST_GALLERY.MANAGE
 * @applicableIdentity APP
 * @fqn wix.fastgallery.draftitem.v1.DraftItemService.UpdateExtendedFields
 */
export async function updateExtendedFields(
  _id: string,
  namespace: string,
  options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
): Promise<
  NonNullablePaths<
    UpdateExtendedFieldsResponse,
    | `draftItem.media.imageOptions.focalPoint.x`
    | `draftItem.media.imageOptions.focalPoint.y`
    | `draftItem.media.type`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    id: _id,
    namespace: namespace,
    namespaceData: options?.namespaceData,
  });

  const reqOpts =
    ambassadorWixFastgalleryDraftitemV1DraftItem.updateExtendedFields(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'draftItem.media.imageOptions.imageInfo' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          id: '$[0]',
          namespace: '$[1]',
          namespaceData: '$[2].namespaceData',
        },
        singleArgumentUnchanged: false,
      },
      ['_id', 'namespace', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateExtendedFieldsOptions {
  /** Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured. */
  namespaceData: Record<string, any> | null;
}

/**
 * Create multiple DraftFastGalleryItems in a single request. Works synchronously.
 * @param draftItems - List of DraftFastGalleryItems to be created
 * @public
 * @documentationMaturity preview
 * @requiredField draftItems
 * @permissionId FAST_GALLERY.MANAGE
 * @applicableIdentity APP
 * @fqn wix.fastgallery.draftitem.v1.DraftItemService.BulkCreateDraftItems
 */
export async function bulkCreateDraftItems(
  draftItems: DraftItem[],
  options?: BulkCreateDraftItemsOptions
): Promise<
  NonNullablePaths<
    BulkCreateDraftItemsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.media.type`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      draftItems: draftItems,
      returnEntity: options?.returnEntity,
      positionFirst: options?.positionFirst,
    }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [{ path: 'draftItems.media.imageOptions.imageInfo' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixFastgalleryDraftitemV1DraftItem.bulkCreateDraftItems(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'results.item.media.imageOptions.imageInfo' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          draftItems: '$[0]',
          returnEntity: '$[1].returnEntity',
          positionFirst: '$[1].positionFirst',
        },
        singleArgumentUnchanged: false,
      },
      ['draftItems', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkCreateDraftItemsOptions {
  /** set to `true` if you wish to receive back the created DraftFastGalleryItems in the response */
  returnEntity?: boolean;
  /** If true, the item will be positioned first in the gallery. If not present or false, the items will be positioned last. */
  positionFirst?: boolean | null;
}

/**
 * Update multiple DraftFastGalleryItems in a single request. Works synchronously.
 * @param draftItems - List of DraftFastGalleryItems to be updated.
 * @public
 * @documentationMaturity preview
 * @requiredField draftItems
 * @requiredField draftItems.draftItem.revision
 * @permissionId FAST_GALLERY.MANAGE
 * @applicableIdentity APP
 * @fqn wix.fastgallery.draftitem.v1.DraftItemService.BulkUpdateDraftItems
 */
export async function bulkUpdateDraftItems(
  draftItems: NonNullablePaths<MaskedDraftItem, `draftItem.revision`, 3>[],
  options?: BulkUpdateDraftItemsOptions
): Promise<
  NonNullablePaths<
    BulkUpdateDraftItemsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.media.type`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      draftItems: draftItems,
      returnEntity: options?.returnEntity,
    }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [{ path: 'draftItems.draftItem.media.imageOptions.imageInfo' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixFastgalleryDraftitemV1DraftItem.bulkUpdateDraftItems(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'results.item.media.imageOptions.imageInfo' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          draftItems: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['draftItems', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateDraftItemsOptions {
  /** set to `true` if you wish to receive back the updated DraftFastGalleryItems in the response */
  returnEntity?: boolean;
}

/**
 * Delete multiple DraftFastGalleryItems in a single request. Works synchronously.
 * @param draftItemIds - DraftFastGalleryItem ids to be deleted
 * @public
 * @documentationMaturity preview
 * @requiredField draftItemIds
 * @permissionId FAST_GALLERY.MANAGE
 * @applicableIdentity APP
 * @fqn wix.fastgallery.draftitem.v1.DraftItemService.BulkDeleteDraftItems
 */
export async function bulkDeleteDraftItems(
  draftItemIds: string[]
): Promise<
  NonNullablePaths<
    BulkDeleteDraftItemsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    draftItemIds: draftItemIds,
  });

  const reqOpts =
    ambassadorWixFastgalleryDraftitemV1DraftItem.bulkDeleteDraftItems(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { draftItemIds: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['draftItemIds']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Removes a DraftItem from TrashBin, permanently deleting it.
 * @param draftItemId - ID of the DraftItem to remove from TrashBin
 * @public
 * @documentationMaturity preview
 * @requiredField draftItemId
 * @permissionId FAST_GALLERY.MANAGE
 * @applicableIdentity APP
 * @fqn wix.fastgallery.draftitem.v1.DraftItemService.RemoveDraftItemFromTrashBin
 */
export async function removeDraftItemFromTrashBin(
  draftItemId: string
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    draftItemId: draftItemId,
  });

  const reqOpts =
    ambassadorWixFastgalleryDraftitemV1DraftItem.removeDraftItemFromTrashBin(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { draftItemId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['draftItemId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Restores a DraftItem from TrashBin. Increments the revision.
 * @param draftItemId - ID of the DraftItem to restore from TrashBin
 * @public
 * @documentationMaturity preview
 * @requiredField draftItemId
 * @permissionId FAST_GALLERY.MANAGE
 * @applicableIdentity APP
 * @fqn wix.fastgallery.draftitem.v1.DraftItemService.RestoreDraftItemFromTrashBin
 */
export async function restoreDraftItemFromTrashBin(
  draftItemId: string
): Promise<
  NonNullablePaths<
    RestoreDraftItemFromTrashBinResponse,
    | `draftItem.media.imageOptions.focalPoint.x`
    | `draftItem.media.imageOptions.focalPoint.y`
    | `draftItem.media.type`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    draftItemId: draftItemId,
  });

  const reqOpts =
    ambassadorWixFastgalleryDraftitemV1DraftItem.restoreDraftItemFromTrashBin(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'draftItem.media.imageOptions.imageInfo' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { draftItemId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['draftItemId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a DraftItem from TrashBin. (DraftItem is kept in TrashBin)
 * @param draftItemId - ID of the DraftItem to retrieve from TrashBin
 * @public
 * @documentationMaturity preview
 * @requiredField draftItemId
 * @permissionId FAST_GALLERY.MANAGE
 * @applicableIdentity APP
 * @fqn wix.fastgallery.draftitem.v1.DraftItemService.GetDeletedDraftItem
 */
export async function getDeletedDraftItem(
  draftItemId: string
): Promise<
  NonNullablePaths<
    GetDeletedDraftItemResponse,
    | `draftItem.media.imageOptions.focalPoint.x`
    | `draftItem.media.imageOptions.focalPoint.y`
    | `draftItem.media.type`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    draftItemId: draftItemId,
  });

  const reqOpts =
    ambassadorWixFastgalleryDraftitemV1DraftItem.getDeletedDraftItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'draftItem.media.imageOptions.imageInfo' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { draftItemId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['draftItemId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a list of DraftItems from TrashBin.
 * If 'draft_item_ids' is provided returns only the requested DraftItems.
 * If query is provided, returns DraftItems that match the query.
 * If both provided, 'draft_item_ids' supersedes the query.
 * @public
 * @documentationMaturity preview
 * @permissionId FAST_GALLERY.MANAGE
 * @applicableIdentity APP
 * @fqn wix.fastgallery.draftitem.v1.DraftItemService.ListDeletedDraftItems
 */
export async function listDeletedDraftItems(
  options?: ListDeletedDraftItemsOptions
): Promise<
  NonNullablePaths<
    ListDeletedDraftItemsResponse,
    `draftItems` | `draftItems.${number}.media.type`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    query: options?.query,
    draftItemIds: options?.draftItemIds,
  });

  const reqOpts =
    ambassadorWixFastgalleryDraftitemV1DraftItem.listDeletedDraftItems(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'draftItems.media.imageOptions.imageInfo' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          query: '$[0].query',
          draftItemIds: '$[0].draftItemIds',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListDeletedDraftItemsOptions {
  /** WQL expression */
  query?: CursorQuery;
  /**
   * List of DraftItem IDs to retrieve (supersedes the query)
   * @format GUID
   * @maxSize 100
   */
  draftItemIds?: string[];
}

/**
 * Move Draft Items to be after another Draft Item.
 * If beforeItemId is not provided, the DraftItem will be positioned first.
 * @param itemIds - DraftItem ids to be moved after `after_item_id`, if not provided, draftItems given will be positioned first
 * @public
 * @documentationMaturity preview
 * @requiredField itemIds
 * @permissionId FAST_GALLERY.MANAGE
 * @applicableIdentity APP
 * @fqn wix.fastgallery.draftitem.v1.DraftItemService.MoveDraftItems
 */
export async function moveDraftItems(
  itemIds: string[],
  options?: MoveDraftItemsOptions
): Promise<
  void & {
    __applicationErrorsType?: MoveDraftItemsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    itemIds: itemIds,
    afterItemId: options?.afterItemId,
  });

  const reqOpts =
    ambassadorWixFastgalleryDraftitemV1DraftItem.moveDraftItems(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          itemIds: '$[0]',
          afterItemId: '$[1].afterItemId',
        },
        singleArgumentUnchanged: false,
      },
      ['itemIds', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface MoveDraftItemsOptions {
  /**
   * Id of the draftItem which the items will be positioned after
   * @format GUID
   */
  afterItemId?: string | null;
}

/**
 * Sort Draft Items by field name and order.
 * @param sorting - DraftItem ids to be sorted by field `sorting.field_name`, in direction `sorting.order`
 * @public
 * @documentationMaturity preview
 * @requiredField options
 * @requiredField options.draftGalleryId
 * @requiredField sorting
 * @requiredField sorting.fieldName
 * @permissionId FAST_GALLERY.MANAGE
 * @applicableIdentity APP
 * @fqn wix.fastgallery.draftitem.v1.DraftItemService.SortDraftItemsBy
 */
export async function sortDraftItemsBy(
  sorting: NonNullablePaths<SortByFieldSorting, `fieldName`, 2>,
  options: NonNullablePaths<SortDraftItemsByOptions, `draftGalleryId`, 2>
): Promise<
  NonNullablePaths<SortDraftItemsByResponse, `jobId`, 2> & {
    __applicationErrorsType?: SortDraftItemsByApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    sorting: sorting,
    draftGalleryId: options?.draftGalleryId,
  });

  const reqOpts =
    ambassadorWixFastgalleryDraftitemV1DraftItem.sortDraftItemsBy(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          sorting: '$[0]',
          draftGalleryId: '$[1].draftGalleryId',
        },
        singleArgumentUnchanged: false,
      },
      ['sorting', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface SortDraftItemsByOptions {
  /**
   * ID of the Gallery whose items are sorted
   * @format GUID
   */
  draftGalleryId: string;
}
